import {
  APP_NAME,
  INVOICE_TAX_NOTE,
  LEGAL_FIELD_LABELS,
  type LegalPage,
  type LegalSettings,
  TERMS_VERSION,
  TRIAL_DAYS,
} from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { EnvBadge, Logo } from "../components/env-badge";
import { LegalLinks } from "../components/legal-links";
import { Skeleton } from "../components/ui/card";
import { orpc } from "../lib/api";
import { formatDate } from "../lib/utils";

/**
 * Imprint, privacy policy and terms. The operator data comes from the admin area
 * (Administration → Rechtliches); the texts are a starting point, not legal advice.
 */

export function useLegal() {
  return useQuery({ ...orpc.legal.get.queryOptions(), staleTime: 60_000 });
}

/** Operator value or a visible gap marker. */
export function Value({
  settings,
  field,
}: {
  settings: LegalSettings;
  field: keyof LegalSettings;
}) {
  const value = settings[field].trim();
  if (value) return <>{value}</>;
  return (
    <mark className="rounded bg-amber-100 px-1 text-amber-900">
      [{LEGAL_FIELD_LABELS[field]} fehlt]
    </mark>
  );
}

/** Free text from the admin area: paragraphs separated by blank lines, no HTML. */
function Paragraphs({ text }: { text: string }) {
  const parts = text
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
  return (
    <>
      {parts.map((part) => (
        <p key={part.slice(0, 40)} className="whitespace-pre-line">
          {part}
        </p>
      ))}
    </>
  );
}

function Address({ settings }: { settings: LegalSettings }) {
  return (
    <p>
      <Value settings={settings} field="operatorName" />
      {settings.legalForm ? `, ${settings.legalForm}` : null}
      <br />
      <Value settings={settings} field="street" />
      <br />
      <Value settings={settings} field="postalCode" /> <Value settings={settings} field="city" />
      {settings.country ? (
        <>
          <br />
          {settings.country}
        </>
      ) : null}
    </p>
  );
}

export function LegalLayout({
  title,
  page,
  children,
}: {
  title: string;
  page: LegalPage | undefined;
  children: (page: LegalPage) => ReactNode;
}) {
  const back = document.referrer.startsWith(window.location.origin) ? document.referrer : "/";
  return (
    <div className="min-h-full bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Logo />
            <EnvBadge />
          </div>
          <a
            href={back}
            className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
          >
            <ArrowLeft className="size-4" /> Zurück
          </a>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10">
        <article className="legal space-y-4 text-[15px] leading-relaxed text-zinc-700 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-zinc-900 [&_h3]:mt-5 [&_h3]:font-semibold [&_h3]:text-zinc-900 [&_li]:ml-5 [&_li]:list-disc [&_a]:text-brand-700 [&_a]:underline">
          <h1 className="text-[40px] leading-[44px] font-bold tracking-tight text-ink">{title}</h1>
          {page ? children(page) : <Skeleton className="h-96" />}
          {page?.updatedAt ? (
            <p className="pt-6 text-sm text-zinc-500">Stand: {formatDate(page.updatedAt)}</p>
          ) : null}
        </article>
        <LegalLinks className="mt-12" />
      </main>
    </div>
  );
}

export function ImprintPage() {
  const { data } = useLegal();
  return (
    <LegalLayout title="Impressum" page={data}>
      {({ settings }) => (
        <>
          <h2>Angaben gemäß § 5 DDG</h2>
          <Address settings={settings} />
          {settings.representative ? <p>Vertreten durch: {settings.representative}</p> : null}

          <h2>Kontakt</h2>
          <p>
            E-Mail: <Value settings={settings} field="email" />
            {settings.phone ? (
              <>
                <br />
                Telefon: {settings.phone}
              </>
            ) : null}
          </p>

          {settings.registerCourt || settings.registerNumber ? (
            <>
              <h2>Registereintrag</h2>
              <p>
                Registergericht: {settings.registerCourt || "–"}
                <br />
                Registernummer: {settings.registerNumber || "–"}
              </p>
            </>
          ) : null}

          {settings.vatId ? (
            <>
              <h2>Umsatzsteuer-ID</h2>
              <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: {settings.vatId}</p>
            </>
          ) : null}

          {settings.contentResponsible ? (
            <>
              <h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
              <p className="whitespace-pre-line">{settings.contentResponsible}</p>
            </>
          ) : null}

          <h2>Verbraucherstreitbeilegung</h2>
          <p>
            Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer
            Verbraucherschlichtungsstelle teilzunehmen.
          </p>

          {settings.extraImprint ? <Paragraphs text={settings.extraImprint} /> : null}
        </>
      )}
    </LegalLayout>
  );
}

export function PrivacyPage() {
  const { data } = useLegal();
  return (
    <LegalLayout title="Datenschutzerklärung" page={data}>
      {({ settings, features }) => (
        <>
          <p>
            Diese Erklärung beschreibt, welche personenbezogenen Daten beim Nutzen dieser Anwendung
            verarbeitet werden, wozu und auf welcher Rechtsgrundlage. Wir verwenden keine Werbung
            und keine Analyse-Dienste von Dritten. Die Aufrufe unserer Website zählen wir selbst und
            anonym: ohne Cookies, ohne Speicherung von IP-Adresse oder Kennung. Erfasst werden nur
            Seite, Gerätetyp, Browser und Herkunft als Zähler, gespeichert auf unserem Server in der
            EU (berechtigtes Interesse, Art. 6 Abs. 1 lit. f DSGVO, Auswertung der Reichweite). Ein
            Widerspruch ist über „Do Not Track“ im Browser möglich.
          </p>

          <h2>1. Verantwortlicher</h2>
          <Address settings={settings} />
          <p>
            E-Mail: <Value settings={settings} field="email" />
          </p>
          {settings.privacyContact ? (
            <p className="whitespace-pre-line">Datenschutz-Kontakt: {settings.privacyContact}</p>
          ) : null}

          <h2>2. Hosting und Server-Protokolle</h2>
          <p>
            Die Anwendung läuft auf einem Server bei{" "}
            <Value settings={settings} field="hostingProvider" />. Mit dem Anbieter besteht ein
            Vertrag zur Auftragsverarbeitung (Art. 28 DSGVO). Beim Aufruf der Seiten verarbeitet der
            Server technisch notwendige Daten: IP-Adresse, Datum und Uhrzeit, aufgerufene Adresse,
            Browser-Kennung und Statuscode. Diese Protokolle dienen dem sicheren Betrieb und der
            Fehlersuche (Art. 6 Abs. 1 lit. f DSGVO). Sie werden automatisch rotiert und dabei
            überschrieben.
          </p>

          <h2>3. Konto und Anmeldung</h2>
          <p>
            Für ein Konto speichern wir Namen, E-Mail-Adresse und das Passwort (nur als sicherer
            Hash). Zu jeder Anmeldung speichern wir eine Sitzung mit IP-Adresse und Browser-Kennung,
            damit du angemeldet bleibst und verdächtige Anmeldungen erkennbar sind. Sitzungen laufen
            nach spätestens 30 Tagen ab. Nutzt du die Zwei-Faktor-Anmeldung, speichern wir den
            Schlüssel deiner Authenticator-App und die Wiederherstellungscodes verschlüsselt.
            Rechtsgrundlage ist die Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO).
          </p>
          {features.googleLogin ? (
            <p>
              Meldest du dich mit Google an, erhalten wir von Google deinen Namen, deine
              E-Mail-Adresse und eine Kennung. Dabei gelten zusätzlich die Datenschutzhinweise von
              Google; Google kann Daten in den USA verarbeiten (Angemessenheitsbeschluss EU-US Data
              Privacy Framework).
            </p>
          ) : null}

          <h2>4. Deine Inhalte</h2>
          <p>
            Dateien, Medien, Schriften, Marken-Einstellungen und Entwürfe, die du oder deine
            Organisation hochladen oder erstellen, speichern wir, um die Anwendung bereitzustellen
            (Art. 6 Abs. 1 lit. b DSGVO). Enthalten diese Inhalte personenbezogene Daten Dritter (z.
            B. Fotos oder Videos von Personen), ist die jeweilige Organisation dafür verantwortlich;
            wir verarbeiten sie in ihrem Auftrag. Einen Vertrag zur Auftragsverarbeitung stellen wir
            auf Anfrage bereit.
          </p>
          <p>
            Die automatischen Untertitel werden ausschließlich auf unserem eigenen Server erzeugt.
            Ton und Video werden dafür an keinen externen Dienst übermittelt. Dasselbe gilt für das
            Freistellen von Bildern (Hintergrund entfernen). Auch Schriften werden von unserem
            Server ausgeliefert; dein Browser baut dafür keine Verbindung zu Google oder anderen
            Anbietern auf.
          </p>

          <h2>5. E-Mails</h2>
          <p>
            Wir senden nur E-Mails, die für die Nutzung nötig sind: Bestätigung der Adresse,
            Passwort-Zurücksetzen, Einladungen in Organisationen und – für Administratoren –
            Betriebs- und Sicherheitshinweise. Einen Newsletter gibt es nicht.
            {features.smtp ? (
              <>
                {" "}
                Für den Versand nutzen wir <Value settings={settings} field="emailProvider" /> als
                Auftragsverarbeiter.
              </>
            ) : null}
          </p>

          <h2>6. Verbundene Apps (KI-Assistenten)</h2>
          <p>
            Du kannst KI-Assistenten wie ChatGPT oder Claude über die MCP-Schnittstelle mit deinem
            Konto verbinden. Das geschieht nur, wenn du es selbst freigibst. Die App erhält dann die
            freigegebenen Daten (z. B. Projekt- und Dateilisten, Inhalte von Entwürfen). Für die
            weitere Verarbeitung ist der Anbieter der jeweiligen App verantwortlich. Du kannst den
            Zugriff jederzeit unter „Konto → Verbundene Apps“ widerrufen.
          </p>

          <h2>7. Cookies und lokaler Speicher</h2>
          <p>
            Wir verwenden nur technisch notwendige Cookies: für die Anmeldung, für den
            Zwischenschritt der Zwei-Faktor-Anmeldung und – falls du das auswählst – um ein Gerät 30
            Tage lang als vertrauenswürdig zu merken. Im lokalen Speicher des Browsers merken wir
            uns Bedienungseinstellungen, etwa die zuletzt geöffnete Organisation. Eine Einwilligung
            ist dafür nicht erforderlich (§ 25 Abs. 2 TDDDG).
          </p>

          <h2>8. Speicherdauer</h2>
          <ul>
            <li>Kontodaten und Inhalte: bis zur Löschung des Kontos bzw. der Organisation.</li>
            <li>
              Gelöschte Dateien: {features.trashDays} Tage im Papierkorb, danach endgültig gelöscht.
            </li>
            <li>Exportierte Dateien (PNG, PDF, MP4): {features.exportDays} Tage.</li>
            <li>
              Datensicherungen: tägliche Sicherungen werden rotiert und nach spätestens sechs
              Monaten gelöscht
              {settings.backupProvider ? (
                <>; verschlüsselte Kopien liegen bei {settings.backupProvider}</>
              ) : null}
              .
            </li>
            <li>
              Protokoll der Administrationsaktionen: zwölf Monate; technische Messwerte des
              Betriebs: 30 Tage.
            </li>
            {features.billing ? (
              <li>
                Rechnungen, Bestellnachweise und Kündigungen: so lange, wie es die handels- und
                steuerrechtlichen Aufbewahrungspflichten verlangen (in der Regel acht bis zehn
                Jahre).
              </li>
            ) : null}
          </ul>

          <h2>9. Empfänger</h2>
          <p>
            Daten erhalten nur die genannten Auftragsverarbeiter (Hosting
            {features.smtp ? ", E-Mail-Versand" : ""}
            {settings.backupProvider ? ", Datensicherung" : ""})
            {features.billing ? ", für Abos der Zahlungsdienstleister Stripe (Nr. 12)" : ""} sowie –
            auf deine Veranlassung – verbundene Apps. Innerhalb einer Organisation sehen deren
            Mitglieder die gemeinsamen Inhalte entsprechend ihrer Rolle. Eine Weitergabe zu
            Werbezwecken findet nicht statt.
          </p>

          <h2>10. Deine Rechte</h2>
          <p>
            Du hast das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art.
            17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und
            Widerspruch gegen Verarbeitungen auf Grundlage berechtigter Interessen (Art. 21). Wende
            dich dafür an die oben genannte Adresse. Du kannst dich außerdem bei einer
            Datenschutz-Aufsichtsbehörde beschweren
            {settings.supervisoryAuthority ? (
              <>, zuständig ist {settings.supervisoryAuthority}</>
            ) : null}
            .
          </p>

          <h2>11. Sicherheit</h2>
          <p>
            Verbindungen sind per TLS verschlüsselt, Passwörter werden nur als Hash gespeichert,
            externe Datensicherungen sind verschlüsselt, und der Zugang zur Administration erfordert
            eine Zwei-Faktor-Anmeldung; jeder Zugriff dort wird protokolliert.
          </p>

          {features.billing ? (
            <>
              <h2>12. Abo und Bezahlung (Stripe)</h2>
              <p>
                Für ein Abo speichern wir, wer es für welche Organisation zu welchem Tarif bestellt
                hat, die Bestätigung der AGB und der Widerrufsbelehrung mit Zeitpunkt, IP-Adresse
                und Browser-Kennung sowie Kündigungen über „Verträge hier kündigen“. Das dient der
                Vertragsdurchführung und dem Nachweis (Art. 6 Abs. 1 lit. b und c DSGVO).
              </p>
              <p>
                Die Zahlung wickelt Stripe Payments Europe Ltd., 1 Grand Canal Street Lower, Grand
                Canal Dock, Dublin, D02 H210, Irland, ab. Zahlungsdaten gibst du direkt bei Stripe
                ein; wir erhalten keine vollständigen Karten- oder Kontonummern. Stripe erhält Name,
                E-Mail-Adresse, Rechnungsadresse, die Organisation, den Tarif und die Zahlungsdaten
                und erstellt die Rechnungen. Für die Zahlungsabwicklung ist Stripe teils selbst
                verantwortlich, etwa zur Betrugsvermeidung und für gesetzliche Pflichten. Stripe
                kann Daten auch an die Stripe, Inc. in den USA übermitteln; dafür gelten der
                Angemessenheitsbeschluss zum EU-US Data Privacy Framework und
                Standardvertragsklauseln. Mehr in der Datenschutzerklärung von Stripe unter
                stripe.com/de/privacy. Rechtsgrundlage ist die Vertragserfüllung (Art. 6 Abs. 1 lit.
                b DSGVO).
              </p>
            </>
          ) : null}

          {settings.extraPrivacy ? <Paragraphs text={settings.extraPrivacy} /> : null}
        </>
      )}
    </LegalLayout>
  );
}

export function TermsPage() {
  const { data } = useLegal();
  return (
    <LegalLayout title="Allgemeine Geschäftsbedingungen" page={data}>
      {({ settings, features }) => (
        <>
          <p>Stand und Version: {TERMS_VERSION.split("-").reverse().join(".")}</p>

          <h2>1. Geltungsbereich und Anbieter</h2>
          <p>
            Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die Nutzung der Web-Anwendung{" "}
            {APP_NAME} zum Gestalten von Social-Media-Posts, Druckdaten und Videos (im Folgenden
            „Dienst“). Anbieter ist <Value settings={settings} field="operatorName" />,{" "}
            <Value settings={settings} field="street" />,{" "}
            <Value settings={settings} field="postalCode" />{" "}
            <Value settings={settings} field="city" /> (im Folgenden „wir“). Sie gelten für
            Verbraucher:innen (§ 13 BGB) und Unternehmer:innen (§ 14 BGB) gleichermaßen, soweit
            nicht ausdrücklich unterschieden wird. Abweichende Bedingungen der Kund:innen gelten
            nicht.
          </p>

          <h2>2. Leistungen</h2>
          <p>
            Wir stellen den Dienst über das Internet bereit. Welche Funktionen und Kontingente
            (Nutzer:innen, Speicher, Untertitel-Minuten, Freistellungen, Brand Kits) enthalten sind,
            richtet sich nach dem gebuchten Tarif, wie er beim Bestellen in der Tarifübersicht
            beschrieben ist. Wir bemühen uns um eine hohe Verfügbarkeit, schulden aber keine
            ununterbrochene Erreichbarkeit; Wartungen und Störungen können zu Unterbrechungen
            führen. Wir entwickeln den Dienst weiter und dürfen Funktionen ändern, soweit das für
            dich zumutbar ist und die vereinbarten Kernfunktionen deines Tarifs erhalten bleiben.
          </p>

          <h2>3. Konto und Organisation</h2>
          <p>
            Für die Nutzung ist ein Konto nötig; die Registrierung ist kostenlos. Die Angaben müssen
            zutreffen, Zugangsdaten sind geheim zu halten, und ein Konto darf nur von einer Person
            genutzt werden. Inhalte werden in Organisationen verwaltet. Ein Abo gehört immer zu
            einer Organisation und wird von deren Owner abgeschlossen, geändert und gekündigt. Owner
            und Administrator:innen vergeben Rollen und sind für die Zusammenarbeit in ihrer
            Organisation verantwortlich.
          </p>

          <h2>4. Vertragsschluss</h2>
          {features.billing ? (
            <p>
              Ein Abo bestellst du in der Anwendung unter Einstellungen → Tarif. Vor der Bestellung
              siehst du eine Übersicht mit Tarif, Preis, Testphase und Laufzeit. Mit einem Klick auf
              „Zahlungspflichtig bestellen“ gibst du ein verbindliches Angebot ab. Anschließend
              hinterlegst du bei unserem Zahlungsdienstleister Stripe ein Zahlungsmittel. Der
              Vertrag kommt zustande, sobald du diesen Schritt abgeschlossen hast und wir dich zur
              Anwendung zurückleiten; bis dahin kannst du den Vorgang jederzeit abbrechen.
              Vertragssprache ist Deutsch. Den Vertragstext (diese AGB in der bei Bestellung
              gültigen Fassung) speichern wir; du kannst ihn jederzeit auf dieser Seite abrufen und
              ausdrucken.
            </p>
          ) : (
            <p>
              Mit der Registrierung kommt ein kostenloser Nutzungsvertrag zustande. Kostenpflichtige
              Tarife sind auf diesem Server derzeit nicht buchbar.
            </p>
          )}

          {features.billing ? (
            <>
              <h2>5. Testphase</h2>
              <p>
                Jede Organisation kann ein Abo einmal {TRIAL_DAYS} Tage lang kostenlos testen. Für
                die Testphase hinterlegst du bereits ein Zahlungsmittel, belastet wird es erst nach
                ihrem Ende. Kündigst du bis zum Ende der Testphase, entstehen keine Kosten.
                Andernfalls geht die Testphase automatisch in das gebuchte, kostenpflichtige Abo
                über.
              </p>

              <h2>6. Preise und Zahlung</h2>
              <p>
                Es gelten die Preise, die bei der Bestellung in der Tarifübersicht angegeben sind.
                Sie sind Endpreise. {INVOICE_TAX_NOTE} Der Preis ist monatlich im Voraus fällig,
                erstmals nach dem Ende der Testphase. Die Zahlung wickelt Stripe Payments Europe
                Ltd. (Irland) ab; es gelten die dort angebotenen Zahlungsarten. Rechnungen erhältst
                du per E-Mail und im Kundenportal (Einstellungen → Tarif → Abo & Rechnungen).
                Schlägt eine Abbuchung fehl, versucht Stripe sie mehrfach erneut. Bleibt sie
                erfolglos, endet das Abo, und die Organisation wird schreibgeschützt (Nr. 9).
              </p>

              <h2>7. Laufzeit, Kündigung und Tarifwechsel</h2>
              <p>
                Das Abo läuft jeweils einen Monat und verlängert sich automatisch um einen weiteren
                Monat, wenn es nicht gekündigt wird. Du kannst jederzeit zum Ende des laufenden
                Abrechnungsmonats kündigen, in der Testphase zu deren Ende: in der Anwendung unter
                Einstellungen → Tarif → Abo & Rechnungen, über die Seite{" "}
                <a href="/kuendigen">„Verträge hier kündigen“</a> oder in Textform (z. B. E-Mail an{" "}
                <Value settings={settings} field="email" />
                ). Wir können mit einer Frist von einem Monat zum Monatsende kündigen. Das Recht zur
                außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt.
              </p>
              <p>
                Den Tarif kannst du jederzeit wechseln. Der Unterschied wird für den Rest des
                laufenden Monats anteilig verrechnet. Überschreiten deine Inhalte nach einem Wechsel
                in einen kleineren Tarif dessen Kontingente, bleibt alles erhalten; nur Neues, das
                über die Kontingente hinausgeht, ist dann nicht möglich.
              </p>

              <h2>8. Preisänderungen</h2>
              <p>
                Preisänderungen kündigen wir mindestens sechs Wochen vor ihrem Inkrafttreten in
                Textform an. Sie gelten nur für die Zeit danach und nur, wenn du ihnen zustimmst.
                Stimmst du nicht zu, läuft das Abo zu den bisherigen Preisen weiter; wir können es
                dann mit der Frist aus Nr. 7 kündigen.
              </p>

              <h2>9. Ende des Abos</h2>
              <p>
                Ohne laufendes Abo ist die Organisation schreibgeschützt: Inhalte lassen sich
                ansehen, aber nicht bearbeiten, hochladen oder exportieren, und Freigabe-Links
                pausieren. Die Inhalte bleiben erhalten und sind mit einem neuen Abo sofort wieder
                nutzbar. Organisationen, die länger als zwölf Monate ohne Abo sind, dürfen wir nach
                einer Ankündigung in Textform mit einer Frist von 30 Tagen löschen.
              </p>

              <h2>10. Widerrufsrecht</h2>
              <p>
                Verbraucher:innen haben ein gesetzliches Widerrufsrecht. Die Einzelheiten stehen in
                der <a href="/widerruf">Widerrufsbelehrung</a>.
              </p>
            </>
          ) : null}

          <h2>{features.billing ? "11" : "5"}. Inhalte der Nutzer:innen</h2>
          <p>
            Die Rechte an hochgeladenen und erstellten Inhalten bleiben bei dir bzw. deiner
            Organisation. Wir erhalten nur die Rechte, die für den Betrieb nötig sind (Speichern,
            Verarbeiten, Darstellen, Exportieren, Sichern). Du stellst sicher, dass du die nötigen
            Rechte an Bildern, Videos, Musik, Schriften und Logos besitzt und dass deine Inhalte
            nicht gegen geltendes Recht verstoßen. Verarbeiten wir für Unternehmer:innen
            personenbezogene Daten Dritter, schließen wir auf Anfrage einen Vertrag zur
            Auftragsverarbeitung (Art. 28 DSGVO).
          </p>

          <h2>{features.billing ? "12" : "6"}. Unzulässige Nutzung</h2>
          <p>
            Untersagt sind insbesondere rechtswidrige Inhalte, Angriffe auf die Infrastruktur, das
            Umgehen von Zugriffsbeschränkungen oder Kontingenten sowie automatisierte Massenzugriffe
            außerhalb der vorgesehenen Schnittstellen. Bei Verstößen können wir Inhalte sperren und
            den Zugang vorübergehend oder bei schwerwiegenden Verstößen dauerhaft sperren.
          </p>

          <h2>{features.billing ? "13" : "7"}. Speicher und Datensicherung</h2>
          <p>
            Gelöschte Inhalte werden nach Ablauf der Papierkorb-Frist endgültig entfernt, Exporte
            nach einigen Tagen. Wir sichern die Daten regelmäßig; wichtige Dateien solltest du
            dennoch zusätzlich selbst aufbewahren.
          </p>

          <h2>{features.billing ? "14" : "8"}. Haftung</h2>
          <p>
            Wir haften unbeschränkt bei Vorsatz und grober Fahrlässigkeit, bei Verletzung von Leben,
            Körper oder Gesundheit und nach dem Produkthaftungsgesetz. Bei leichter Fahrlässigkeit
            haften wir nur für die Verletzung wesentlicher Vertragspflichten (Pflichten, deren
            Erfüllung die Durchführung des Vertrags erst ermöglicht und auf deren Einhaltung du
            regelmäßig vertrauen darfst), begrenzt auf den vorhersehbaren, vertragstypischen
            Schaden. Die Prüfung der Druckdaten vor einer Bestellung bei einer Druckerei ist Sache
            der Nutzer:innen.
          </p>

          <h2>{features.billing ? "15" : "9"}. Datenschutz</h2>
          <p>
            Informationen zur Verarbeitung personenbezogener Daten stehen in der{" "}
            <a href="/datenschutz">Datenschutzerklärung</a>.
          </p>

          <h2>{features.billing ? "16" : "10"}. Änderungen dieser AGB</h2>
          <p>
            Änderungen dieser AGB teilen wir mindestens sechs Wochen vor ihrem Inkrafttreten in
            Textform mit. Sie gelten nur, wenn du zustimmst. Stimmst du nicht zu, gelten die
            bisherigen Bedingungen weiter; wir können den Vertrag dann mit der Frist aus Nr. 7
            kündigen.
          </p>

          <h2>{features.billing ? "17" : "11"}. Schlussbestimmungen</h2>
          <p>
            Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Für Verbraucher:innen gilt
            diese Rechtswahl nur, soweit ihnen dadurch nicht der Schutz zwingender Vorschriften des
            Staates entzogen wird, in dem sie ihren gewöhnlichen Aufenthalt haben. Sind Kund:innen
            Kaufleute, ist Gerichtsstand unser Sitz. Wir sind nicht bereit und nicht verpflichtet,
            an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            Sollte eine Bestimmung unwirksam sein, bleibt der Rest wirksam.
          </p>

          {settings.extraTerms ? <Paragraphs text={settings.extraTerms} /> : null}
        </>
      )}
    </LegalLayout>
  );
}

/**
 * Model withdrawal notice and form (Anlage 1 and 2 zu Art. 246a EGBGB), in the
 * statutory "Sie" wording so the legal presumption of correctness applies.
 */
export function WithdrawalPage() {
  const { data } = useLegal();
  return (
    <LegalLayout title="Widerrufsbelehrung" page={data}>
      {({ settings }) => (
        <>
          <p>Verbraucher:innen steht ein Widerrufsrecht nach folgender Maßgabe zu.</p>

          <h2>Widerrufsrecht</h2>
          <p>
            Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu
            widerrufen. Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses.
          </p>
          <p>
            Um Ihr Widerrufsrecht auszuüben, müssen Sie uns (
            <Value settings={settings} field="operatorName" />,{" "}
            <Value settings={settings} field="street" />,{" "}
            <Value settings={settings} field="postalCode" />{" "}
            <Value settings={settings} field="city" />, E-Mail:{" "}
            <Value settings={settings} field="email" />
            {settings.phone ? `, Telefon: ${settings.phone}` : ""}) mittels einer eindeutigen
            Erklärung (z. B. ein mit der Post versandter Brief oder E-Mail) über Ihren Entschluss,
            diesen Vertrag zu widerrufen, informieren. Sie können dafür das beigefügte
            Muster-Widerrufsformular verwenden, das jedoch nicht vorgeschrieben ist.
          </p>
          <p>
            Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die Ausübung
            des Widerrufsrechts vor Ablauf der Widerrufsfrist absenden.
          </p>

          <h2>Folgen des Widerrufs</h2>
          <p>
            Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen
            erhalten haben, einschließlich der Lieferkosten (mit Ausnahme der zusätzlichen Kosten,
            die sich daraus ergeben, dass Sie eine andere Art der Lieferung als die von uns
            angebotene, günstigste Standardlieferung gewählt haben), unverzüglich und spätestens
            binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren
            Widerruf dieses Vertrags bei uns eingegangen ist. Für diese Rückzahlung verwenden wir
            dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben, es
            sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart; in keinem Fall werden
            Ihnen wegen dieser Rückzahlung Entgelte berechnet.
          </p>
          <p>
            Haben Sie verlangt, dass die Dienstleistungen während der Widerrufsfrist beginnen
            sollen, so haben Sie uns einen angemessenen Betrag zu zahlen, der dem Anteil der bis zu
            dem Zeitpunkt, zu dem Sie uns von der Ausübung des Widerrufsrechts hinsichtlich dieses
            Vertrags unterrichten, bereits erbrachten Dienstleistungen im Vergleich zum Gesamtumfang
            der im Vertrag vorgesehenen Dienstleistungen entspricht.
          </p>
          <p>
            Hinweis: Die Testphase der ersten {TRIAL_DAYS} Tage ist kostenlos. Widerrufen Sie in
            dieser Zeit, entstehen Ihnen keine Kosten.
          </p>
          <p>– Ende der Widerrufsbelehrung –</p>

          <h2>Muster-Widerrufsformular</h2>
          <p>
            (Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte dieses Formular aus und
            senden Sie es zurück.)
          </p>
          <div className="rounded-lg border border-zinc-200 bg-white p-5">
            <p>
              An <Value settings={settings} field="operatorName" />,{" "}
              <Value settings={settings} field="street" />,{" "}
              <Value settings={settings} field="postalCode" />{" "}
              <Value settings={settings} field="city" />, E-Mail:{" "}
              <Value settings={settings} field="email" />:
            </p>
            <p>
              Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen Vertrag über den
              Kauf der folgenden Waren (*)/die Erbringung der folgenden Dienstleistung (*)
            </p>
            <p>_______________________________________________</p>
            <p>Bestellt am (*)/erhalten am (*): ____________________</p>
            <p>Name des/der Verbraucher(s): ____________________</p>
            <p>Anschrift des/der Verbraucher(s): ____________________</p>
            <p>
              Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier):
              ____________________
            </p>
            <p>Datum: ____________________</p>
            <p className="text-sm text-zinc-500">(*) Unzutreffendes streichen.</p>
          </div>
        </>
      )}
    </LegalLayout>
  );
}
